import React from 'react';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import Button from './Button';
import Modal from './Modal';

export interface EditOrganizationFormData {
  name: string;
  slug: string;
  slogan: string;
  inviteEmail?: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  slogan?: string;
}

interface OrganizationFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization?: Organization;
  onSave: (formData: EditOrganizationFormData) => Promise<void>;
  mode: 'create' | 'edit';
}

const OrganizationFormModal: React.FC<OrganizationFormModalProps> = ({
  isOpen,
  onClose,
  organization,
  onSave,
  mode
}) => {
  const [formData, setFormData] = React.useState<EditOrganizationFormData>({
    name: '',
    slug: '',
    slogan: ''
  });

  // Reset form data when modal opens or mode/organization changes
  React.useEffect(() => {
    if (mode === 'edit' && organization) {
      setFormData({
        name: organization.name,
        slug: organization.slug,
        slogan: organization.slogan || '',
        inviteEmail: ''
      });
    } else if (mode === 'create') {
      setFormData({
        name: '',
        slug: '',
        slogan: '',
        inviteEmail: ''
      });
    }
  }, [organization, mode, isOpen]);

  const handleFormDataChange =
    (field: keyof EditOrganizationFormData) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setFormData({ ...formData, [field]: e.target.value });
    };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    onSave(formData);
  };

  const footerContent = (
    <div className="flex justify-end space-x-3">
      <Button type="button" onClick={onClose} variant="secondary">
        Cancelar
      </Button>
      <Button type="submit" form="edit-organization-form" leftIcon={faCheck} variant="primary">
        {mode === 'create' ? 'Criar Organização' : 'Salvar Alterações'}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Nova Organização' : 'Editar Organização'}
      footer={footerContent}
    >
      <form id="edit-organization-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome da Organização
          </label>
          <input
            type="text"
            required={mode === 'edit'}
            value={formData.name}
            onChange={handleFormDataChange('name')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL)</label>
          <input
            type="text"
            required={mode === 'edit'}
            placeholder={mode === 'create' ? 'Deixe em branco para gerar automaticamente' : ''}
            value={formData.slug}
            onChange={handleFormDataChange('slug')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Slogan</label>
          <input
            type="text"
            value={formData.slogan}
            onChange={handleFormDataChange('slogan')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {mode === 'create' && (
          <InviteEmailField formData={formData} onChange={handleFormDataChange('inviteEmail')} />
        )}
      </form>
    </Modal>
  );
};

// Add email input field for create mode
const InviteEmailField = ({
  formData,
  onChange
}: {
  formData: EditOrganizationFormData;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Email para Convite (Opcional)
    </label>
    <input
      type="email"
      value={formData.inviteEmail || ''}
      onChange={onChange}
      placeholder="usuario@exemplo.com"
      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  </div>
);

export default OrganizationFormModal;
